import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { sortBy } from 'lodash-es';
import { useNavigate, useParams } from 'react-router-dom';

import {
  MsTeamsTargetingClient,
  MSTeamsUser,
  RosterClient,
  SlackTargetingClient,
  SlackUser,
  StudyClient,
  StudyMSTeamsTarget,
  StudyPrompt,
  StudySlackTarget,
  StudyTeam,
  Team,
} from 'jf/api';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { CATCH_ALL_TEAM_REF } from '../study/question/QuestionCardDemographic';

export type StudyBuilderStep =
  | 'choose-study'
  | 'customize-study'
  | 'target-respondents'
  | 'publish-study'
  | 'schedule-study';

export const STUDY_BUILDER_STEPS: StudyBuilderStep[] = [
  'choose-study',
  'customize-study',
  'target-respondents',
  'schedule-study',
  'publish-study',
];

export type StudyStep =
  | {
      type: 'INTRO' | 'DEMOGRAPHIC' | 'VOTING';
    }
  | { type: 'PROMPT'; key: string };

type StudyBuilderState = {
  step: StudyBuilderStep; // step in Study Builder
  studyRef?: string;
  creatingStudy: boolean; // CreateStudy query firing
  updatingStudy: boolean; // UpdateStudy query firing
  studyStep: StudyStep; // step in Study
  // editable study attributes
  name: string;
  prompts: StudyPrompt[];
  welcomeMessage: string;
  loaded: boolean; // identifies when editable study attributes have been populated
  teams: Team[];
  slackTargetingEnabled: boolean;
  slackTargets: StudySlackTarget[];
  studyTeams: StudyTeam[];
  slackUsers: SlackUser[]; // slack users targeted in the study
  slackUsersLoaded: boolean; // slack users query complete
  targetResponses?: number | null;
  enableScheduledReminders?: boolean;
  scheduledCloseDate?: string | null;
  // MS Teams study builder attributes
  msteamsTargetingEnabled: boolean;
  msteamsTargets: StudyMSTeamsTarget[];
  msteamsUsers: MSTeamsUser[]; // msteams users targeted in the study
  msteamsUsersLoaded: boolean; // msteams users query complete
};

const DEFAULT_STUDY_BUILDER_STATE: StudyBuilderState = {
  step: 'choose-study',
  creatingStudy: false,
  updatingStudy: false,
  studyStep: { type: 'INTRO' },
  name: '',
  prompts: [],
  welcomeMessage: '',
  loaded: false,
  teams: [],
  slackTargetingEnabled: false,
  slackTargets: [],
  studyTeams: [],
  slackUsers: [],
  slackUsersLoaded: false,
  msteamsTargetingEnabled: false,
  msteamsTargets: [],
  msteamsUsers: [],
  msteamsUsersLoaded: false,
};

type _StudyBuilderStateContext = StudyBuilderState & {
  update: (state: Partial<StudyBuilderState>) => void;
  targetingEnabled: boolean;
};

const StudyBuilderStateContext = React.createContext<_StudyBuilderStateContext>({
  ...DEFAULT_STUDY_BUILDER_STATE,
  update: () => {},
  targetingEnabled: false,
});

export const StudyBuilderStateProvider: React.FC = (props) => {
  const { studyRef, step: stepParam } = useParams<{ studyRef: string; step: string }>();
  const navigate = useNavigate();
  const { msTeamsTargeting } = useClientFlags();
  const { data: slackStatus } = useClientQuery(SlackTargetingClient.getSlackStatus, null, {
    enabled: !msTeamsTargeting,
  });
  const { data: msteamsStatus } = useClientQuery(MsTeamsTargetingClient.getMsteamsStatus, null, {
    enabled: msTeamsTargeting,
  });

  const [state, setState] = useState<StudyBuilderState>({
    ...DEFAULT_STUDY_BUILDER_STATE,
    step: studyRef ? 'customize-study' : 'choose-study',
  });
  const [stepParamLoaded, setStepParamLoaded] = useState(false);

  const updateState = (partialState: Partial<StudyBuilderState>) => {
    setState((state) => ({ ...state, ...partialState }));
  };

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });
  const { data: teams } = useClientQuery(RosterClient.getTeams, {});
  const { data: slackTargets } = useClientQuery(SlackTargetingClient.getStudySlackTargets, {
    studyRef,
  });
  const { data: msteamsTargets } = useClientQuery(MsTeamsTargetingClient.getStudyMsteamsTargets, {
    studyRef,
  });
  const { data: studyTeams } = useClientQuery(StudyClient.getStudyTeams, { studyRef });

  const { data: targetedSlackRespondents, isError: targetedSlackRespondentsError } = useClientQuery(
    RosterClient.getRosterTargets,
    {
      studyRef,
      slackSelected: true,
    },
    { enabled: !msTeamsTargeting }
  );
  const targetedMsteamsQuery = useClientQuery(
    RosterClient.getRosterTargets,
    {
      studyRef,
      msteamsSelected: true,
    },
    {
      enabled: msTeamsTargeting,
    }
  );
  const targetedMsteamsRespondents = targetedMsteamsQuery.data;
  const targetedMsteamsRespondentsError = targetedMsteamsQuery.isError;

  const context: _StudyBuilderStateContext = {
    ...state,
    studyRef,
    update: updateState,
    targetingEnabled: state.slackTargetingEnabled || state.msteamsTargetingEnabled,
  };

  useEffect(() => {
    // populate editable states when study first loads
    if (study && !state.loaded) {
      updateState({
        loaded: true,
        name: study.name,
        prompts: study.prompts,
        welcomeMessage: study.welcomeMessage,
        targetResponses: study.targetResponses,
        enableScheduledReminders: study.enableScheduledReminders,
        scheduledCloseDate: study.scheduledCloseDate,
      });
      if (study.targetResponses) {
        updateState({ slackTargetingEnabled: false, msteamsTargetingEnabled: false });
      }
    }
  }, [study]);

  useEffect(() => {
    // populate editable teams
    if (teams) {
      updateState({
        teams: sortBy(teams, 'value')
          .filter((team) => team.ref !== CATCH_ALL_TEAM_REF)
          .map((team) => ({ ...team })),
      });
    }
  }, [teams]);

  useEffect(() => {
    // populate editable study slack targets
    if (slackTargets && slackStatus) {
      const targetingEnabled =
        slackTargets.length > 0 && slackStatus.connected && !msTeamsTargeting;

      // Seed visible Slack users immediately from slackTargets when cloning,
      // so we don't block on the roster call to clear the spinner.
      // These are replaced later when targetedSlackRespondents arrives.
      if (targetingEnabled && !state.slackUsersLoaded) {
        const seededSlackUsers: SlackUser[] = (slackTargets as StudySlackTarget[]).map(
          (t: StudySlackTarget) => ({
            // We do not have the SlackUser publicReferenceId here; reuse id for stable rendering
            publicReferenceId: t.id,
            slackUserId: t.id,
            email: t.email ?? '',
            realName: t.displayName ?? t.name ?? '',
            userName: t.displayName ?? t.name ?? (t.email ? t.email.split('@')[0] : ''),
            profileImageUrl: t.image ?? '',
          })
        );

        const defaultCloseDate =
          !state.scheduledCloseDate || !dayjs(state.scheduledCloseDate).isValid()
            ? dayjs().add(2, 'week').startOf('day').toISOString()
            : state.scheduledCloseDate;

        updateState({
          slackTargetingEnabled: targetingEnabled,
          slackTargets,
          slackUsers: seededSlackUsers,
          slackUsersLoaded: true,
          scheduledCloseDate: defaultCloseDate,
          enableScheduledReminders: true,
        });
      } else {
        updateState({
          slackTargetingEnabled: targetingEnabled,
          slackTargets,
        });
      }
    }
  }, [slackTargets, slackStatus, msTeamsTargeting]);

  useEffect(() => {
    // populate study slack users
    if (targetedSlackRespondents && slackStatus) {
      updateState({
        slackTargetingEnabled:
          targetedSlackRespondents.length > 0 && slackStatus.connected && !msTeamsTargeting,
        slackUsers: targetedSlackRespondents
          ?.filter((respondent) => respondent.slack !== null)
          .map((respondent) => respondent.slack) as SlackUser[],
        slackUsersLoaded: true,
      });
    }
  }, [targetedSlackRespondents, slackStatus, msTeamsTargeting]);

  useEffect(() => {
    // Fail-safe: if roster query errors out, clear the spinner
    if (targetedSlackRespondentsError && slackStatus) {
      updateState({ slackUsersLoaded: true });
    }
  }, [targetedSlackRespondentsError, slackStatus]);

  useEffect(() => {
    // populate editable study msteams targets
    if (msteamsTargets && msteamsStatus) {
      const targetingEnabled = msteamsStatus.connected && msTeamsTargeting;

      // Seed visible MS Teams users immediately from msteamsTargets when present,
      // so we don't block on the roster call to clear the spinner.
      if (targetingEnabled && !state.msteamsUsersLoaded) {
        const seededMsteamsUsers: MSTeamsUser[] = (msteamsTargets as StudyMSTeamsTarget[]).map(
          (t: StudyMSTeamsTarget) => ({
            id: t.id,
            email: t.email ?? '',
            displayName: t.displayName ?? '',
            name: t.name ?? (t.email ? t.email.split('@')[0] : ''),
            image: t.image ?? [],
          })
        );

        const defaultCloseDate =
          !state.scheduledCloseDate || !dayjs(state.scheduledCloseDate).isValid()
            ? dayjs().add(2, 'week').startOf('day').toISOString()
            : state.scheduledCloseDate;

        updateState({
          msteamsTargetingEnabled: targetingEnabled,
          msteamsTargets,
          msteamsUsers: seededMsteamsUsers,
          msteamsUsersLoaded: true,
          scheduledCloseDate: defaultCloseDate,
          enableScheduledReminders: true,
        });
      } else {
        updateState({
          msteamsTargetingEnabled: targetingEnabled,
          msteamsTargets,
        });
      }
    }
  }, [msteamsTargets, msteamsStatus, msTeamsTargeting]);

  useEffect(() => {
    // populate study msteams users
    if (targetedMsteamsRespondents && msteamsStatus) {
      updateState({
        msteamsTargetingEnabled: msteamsStatus.connected && msTeamsTargeting,
        msteamsUsers: targetedMsteamsRespondents
          ?.filter((respondent) => respondent.msteams !== null)
          .map((respondent) => respondent.msteams) as MSTeamsUser[],
        msteamsUsersLoaded: true,
      });
    }
  }, [targetedMsteamsRespondents, msteamsStatus, msTeamsTargeting]);
  // Fail-safe: if roster query errors out, clear the spinner for MS Teams as well
  useEffect(() => {
    if (targetedMsteamsRespondentsError && msteamsStatus) {
      updateState({ msteamsUsersLoaded: true });
    }
  }, [targetedMsteamsRespondentsError, msteamsStatus]);

  useEffect(() => {
    // populate editable study teams
    if (studyTeams) {
      updateState({ studyTeams });
    }
  }, [studyTeams]);

  useEffect(() => {
    // if step param is provided, go to that step
    if (stepParam) {
      updateState({ step: stepParam as StudyBuilderStep });
    }
    setStepParamLoaded(true);
  }, [stepParam]);

  useEffect(() => {
    // make sure step param is updated when changing steps
    if (stepParamLoaded && studyRef && state.step !== stepParam) {
      navigate(`/study/${studyRef}/build/${state.step}`);
    }
  }, [stepParamLoaded, state.step]);

  return (
    <StudyBuilderStateContext.Provider value={context}>
      {props.children}
    </StudyBuilderStateContext.Provider>
  );
};

export const useStudyBuilderState = () => React.useContext(StudyBuilderStateContext);
